import type { CalendarRange } from './calendar-item';

// Calendar arithmetic in the viewer's own timezone. Days are local midnights, built with the Date
// constructor so a DST change makes a 23- or 25-hour day rather than a day that starts at 01:00.

export type CalendarView = 'day' | 'week' | 'month';

export const CALENDAR_VIEWS: readonly CalendarView[] = ['day', 'week', 'month'];

export function startOfDay(at: number): number {
  const date = new Date(at);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

export function addDays(day: number, days: number): number {
  const date = new Date(day);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days).getTime();
}

export function addMonths(day: number, months: number): number {
  const date = new Date(day);
  return new Date(date.getFullYear(), date.getMonth() + months, 1).getTime();
}

export function sameDay(a: number, b: number): boolean {
  return startOfDay(a) === startOfDay(b);
}

/** 0 = Sunday … 6 = Saturday, from the locale's own week where the runtime knows it. */
export function weekStartsOn(locale: string | undefined): number {
  try {
    const info = new Intl.Locale(locale ?? 'en-US') as Intl.Locale & {
      weekInfo?: { firstDay: number };
      getWeekInfo?: () => { firstDay: number };
    };
    const firstDay = info.getWeekInfo?.().firstDay ?? info.weekInfo?.firstDay;
    if (firstDay) return firstDay % 7;
  } catch {
    // An unknown tag falls through to the default.
  }
  return locale?.toLowerCase().startsWith('he') || locale?.toLowerCase().startsWith('en-us') ? 0 : 1;
}

export function startOfWeek(at: number, firstDay: number): number {
  const day = startOfDay(at);
  const offset = (new Date(day).getDay() - firstDay + 7) % 7;
  return addDays(day, -offset);
}

/** The days a view draws, as local midnights. A month draws whole weeks, 5 or 6 of them. */
export function viewDays(view: CalendarView, anchor: number, firstDay: number): number[] {
  if (view === 'day') return [startOfDay(anchor)];
  if (view === 'week') {
    const start = startOfWeek(anchor, firstDay);
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }
  const first = new Date(anchor);
  const monthStart = new Date(first.getFullYear(), first.getMonth(), 1).getTime();
  const nextMonth = addMonths(monthStart, 1);
  const days: number[] = [];
  for (let day = startOfWeek(monthStart, firstDay); day < nextMonth || days.length % 7 !== 0; day = addDays(day, 1)) {
    days.push(day);
  }
  return days;
}

export function viewRange(view: CalendarView, anchor: number, firstDay: number): CalendarRange {
  const days = viewDays(view, anchor, firstDay);
  return { from: days[0], to: addDays(days[days.length - 1], 1) };
}

/** The anchor one step back or forward: a day, a week or a month. */
export function stepAnchor(view: CalendarView, anchor: number, direction: -1 | 1): number {
  if (view === 'day') return addDays(startOfDay(anchor), direction);
  if (view === 'week') return addDays(startOfDay(anchor), 7 * direction);
  return addMonths(anchor, direction);
}

/** The day a view lands on after a step: today when it is in view, else the view's first day. */
export function focusDayFor(view: CalendarView, anchor: number, firstDay: number, now: number): number {
  const days = viewDays(view, anchor, firstDay);
  const today = startOfDay(now);
  if (view === 'month') {
    const month = new Date(anchor).getMonth();
    const inMonth = days.filter((day) => new Date(day).getMonth() === month);
    return inMonth.includes(today) ? today : inMonth[0];
  }
  return days.includes(today) ? today : days[0];
}

/** `YYYY-MM-DD` for the URL, in local time. */
export function toDayParam(day: number): string {
  const date = new Date(day);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function fromDayParam(param: unknown): number | undefined {
  if (typeof param !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(param)) return undefined;
  const [year, month, day] = param.split('-').map(Number);
  const at = new Date(year, month - 1, day).getTime();
  return Number.isFinite(at) ? at : undefined;
}
